<template>
  <AModal
    :model-value="modelValue"
    :title="conf.title"
    :sub="conf.sub"
    size="xl"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="tf-modal__bar">
      <div class="tf-modal__count">총 <strong>{{ rows.length }}</strong>개 {{ conf.noun }}</div>
      <ABtn size="sm" kind="primary" icon="add" @click="openAdd">{{ conf.noun }} 추가</ABtn>
    </div>

    <!-- 추가 / 수정 겸용 인라인 폼 -->
    <div v-if="form" class="tf-modal__form">
      <div class="tf-modal__form-title">
        {{ form.mode === 'add' ? `${conf.noun} 추가` : `${conf.noun} 수정 — ${original.code}` }}
      </div>

      <div class="tf-modal__form-grid">
        <label class="tf-modal__field">
          <span>코드 <b class="tf-modal__req">*</b></span>
          <input
            ref="codeInput"
            v-model.trim="form.code"
            class="p-input"
            :class="{ 'is-invalid': isDuplicateCode }"
            type="text"
            :disabled="form.mode === 'edit'"
            :placeholder="conf.codePlaceholder"
          />
          <small v-if="isDuplicateCode" class="tf-modal__err">이미 존재하는 코드입니다. (대소문자 구분 없음)</small>
          <small v-else class="tf-modal__hint">{{ conf.codeHint }}</small>
        </label>

        <template v-if="conf.hasGroup">
          <label class="tf-modal__field tf-modal__field--num">
            <span>그룹 <small>(선택)</small></span>
            <input v-model="form.re_group" class="p-input" type="number" placeholder="비우면 맨 뒤" />
          </label>
          <label class="tf-modal__field tf-modal__field--num">
            <span>깊이</span>
            <input v-model="form.re_level" class="p-input" type="number" placeholder="1" />
          </label>
          <label class="tf-modal__field tf-modal__field--num">
            <span>순서</span>
            <input v-model="form.re_num" class="p-input" type="number" placeholder="1" />
          </label>
        </template>
        <template v-else>
          <label class="tf-modal__field tf-modal__field--num">
            <span>노출순서 <small>(선택)</small></span>
            <input v-model="form.sort_order" class="p-input" type="number" placeholder="비우면 맨 뒤" />
          </label>
        </template>
      </div>

      <div class="tf-modal__names">
        <span class="tf-modal__names-title">표시명 <small>— ko만 필수. 비운 언어는 조회 시 ko로 대체 표시됩니다.</small></span>
        <div class="tf-modal__names-grid">
          <label v-for="nl in NAME_LANGS" :key="nl.key" class="tf-modal__field">
            <span>{{ nl.label }} ({{ nl.key }}) <b v-if="nl.required" class="tf-modal__req">*</b></span>
            <input v-model.trim="form.names[nl.key]" class="p-input" type="text" :placeholder="`${nl.label} 표시명`" />
          </label>
        </div>
      </div>

      <div class="tf-modal__form-actions">
        <ABtn size="sm" kind="ghost" @click="cancelForm">취소</ABtn>
        <ABtn size="sm" kind="primary" :disabled="!canSave" :loading="saving" @click="save">
          {{ form.mode === 'add' ? '추가' : '수정 저장' }}
        </ABtn>
      </div>
    </div>

    <div class="tf-modal__table">
      <ATable :columns="columns" :rows="rows" row-key="idx" striped bordered>
        <template #cell:code="{ row }">
          <code class="tf-modal__code">{{ row.code }}</code>
        </template>

        <template #cell:name="{ row }">
          <span class="tf-modal__name">{{ row.name }}</span>
        </template>

        <template #cell:textbook_count="{ row }">
          <span :class="['tf-modal__cnt', { 'is-zero': !row.textbook_count }]">{{ row.textbook_count }}권</span>
        </template>

        <template #cell:_actions="{ row }">
          <div class="tf-modal__row-actions">
            <ABtn size="sm" kind="ghost" icon="edit" icon-only title="수정" @click="openEdit(row)" />
            <span
              class="tf-modal__del-wrap"
              :title="row.textbook_count > 0
                ? `이 ${conf.noun}로 지정된 교재 ${row.textbook_count}권을 먼저 해제해야 삭제할 수 있습니다`
                : '삭제'"
            >
              <ABtn
                size="sm"
                kind="danger"
                icon="delete"
                icon-only
                :disabled="row.textbook_count > 0"
                @click="remove(row)"
              />
            </span>
          </div>
        </template>

        <template #empty>등록된 {{ conf.noun }}가 없습니다.</template>
      </ATable>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">닫기</ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";

// 표시명 입력 6개 언어. 지원 언어 코드표와 같은 체계입니다.
const NAME_LANGS = [
  { key: "ko", label: "한국어", required: true },
  { key: "en", label: "영어" },
  { key: "ja", label: "일본어" },
  { key: "vi", label: "베트남어" },
  { key: "zh", label: "중국어" },
  { key: "ru", label: "러시아어" },
];

// facet별 차이는 여기서만 관리합니다. 목록/폼/저장 흐름은 완전히 공용입니다.
const FACET_CONF = {
  field: {
    title: "도서분야 관리",
    sub: "교재를 분류하는 도서분야 마스터입니다. 교재별 지정은 교재 편집 화면에서 합니다.",
    noun: "분야",
    url: "/textbook/categories",
    listQueryKey: "field_idx",
    codePlaceholder: "예: exam",
    codeHint: "등록 후 변경할 수 없습니다. 영문 소문자 코드를 쓰세요.",
    hasGroup: true, // re_group / re_level / re_num
  },
  language: {
    title: "대상언어 관리",
    sub: "교재가 지원하는 학습자 모국어 마스터입니다.",
    noun: "언어",
    url: "/textbook/targetLanguages",
    listQueryKey: "language_idx",
    codePlaceholder: "예: en, zh-CN",
    codeHint: "지원 언어 코드표(교재 관리 > 언어 코드표)와 같은 체계를 쓰세요. '기타'만 etc 입니다.",
    hasGroup: false, // sort_order
  },
};

const normalizeCode = (v) => (v || "").trim().toLowerCase();

// names_json 은 JSON "문자열"입니다. 6칸 폼 초기값으로 쓰려면 파싱이 필요합니다.
const parseNames = (namesJson) => {
  let parsed = {};
  try {
    parsed = JSON.parse(namesJson || "{}") || {};
  } catch (e) {
    parsed = {};
  }
  const names = {};
  NAME_LANGS.forEach((nl) => {
    names[nl.key] = parsed[nl.key] || "";
  });
  return names;
};

export default {
  name: "TextbookFacetModal",
  components: { AModal, ABtn, ATable },
  props: {
    modelValue: { type: Boolean, default: false },
    facet: {
      type: String,
      default: "field",
      validator: (v) => ["field", "language"].includes(v),
    },
  },
  emits: ["update:modelValue", "changed"],
  data() {
    return {
      NAME_LANGS,
      rows: [],
      loaded: false, // 현재 facet의 목록 조회가 성공했는지 — 닫힘 동기화 emit 여부 판단용
      form: null, // null=목록만, { mode:'add'|'edit', idx, code, names:{6칸}, re_group|sort_order, ... }
      original: null,
      saving: false,
    };
  },
  computed: {
    conf() {
      return FACET_CONF[this.facet] || FACET_CONF.field;
    },
    columns() {
      const cols = [
        { key: "code", label: "코드", width: 110 },
        { key: "name", label: "표시명(ko)" },
      ];
      if (this.conf.hasGroup) {
        cols.push(
          { key: "re_group", label: "그룹", align: "center", width: 60 },
          { key: "re_level", label: "깊이", align: "center", width: 60 },
        );
      } else {
        cols.push({ key: "sort_order", label: "순서", align: "center", width: 60 });
      }
      cols.push(
        { key: "textbook_count", label: "교재 수", align: "center", width: 80 },
        { key: "_actions", label: "관리", align: "right", width: 90 },
      );
      return cols;
    },
    // 서버도 대소문자 무시 + 공백 제거 기준으로 409를 판정합니다.
    isDuplicateCode() {
      if (!this.form || !this.form.code) return false;
      const target = normalizeCode(this.form.code);
      return this.rows.some((r) => normalizeCode(r.code) === target && r.idx !== this.form.idx);
    },
    // 수정 모드에서 변경분이 없으면 요청을 보내지 않습니다 (400 "수정할 내용이 없습니다" 예방)
    isDirty() {
      if (!this.form || this.form.mode === "add") return true;
      const f = this.form;
      const o = this.original;
      if (NAME_LANGS.some((nl) => f.names[nl.key] !== o.names[nl.key])) return true;
      if (this.conf.hasGroup) {
        return (
          String(f.re_group) !== String(o.re_group) ||
          String(f.re_level) !== String(o.re_level) ||
          String(f.re_num) !== String(o.re_num)
        );
      }
      return String(f.sort_order) !== String(o.sort_order);
    },
    canSave() {
      if (!this.form) return false;
      if (!this.form.names.ko) return false;
      if (this.form.mode === "add") return !!this.form.code && !this.isDuplicateCode;
      return this.isDirty;
    },
  },
  watch: {
    modelValue(v) {
      if (v) {
        // 열 때마다 최신본 재조회 — 다른 관리자가 그 사이 바꿨을 수 있어 캐시를 쓰지 않습니다.
        // rows를 먼저 비웁니다. 이전 facet의 행이 남은 채 조회가 실패하면, 그 행의 수정·삭제가
        // 새 facet의 엔드포인트로 나가 idx가 겹치는 무관한 항목을 건드릴 수 있습니다.
        this.rows = [];
        this.loaded = false;
        this.cancelForm();
        this.fetchRows();
      } else if (this.loaded) {
        // 닫힘 시에도 부모 폼의 체크박스 옵션을 동기화합니다.
        // CRUD 없이 열람만 해도 다른 관리자의 변경분이 폼 옵션에 반영됩니다. (부모는 옵션·체크만 갱신)
        this.$emit("changed", { facet: this.facet, list: this.rows, deletedIdx: null });
      }
    },
  },
  methods: {
    emptyForm(mode) {
      const base = { mode, idx: null, code: "", names: parseNames(null) };
      if (this.conf.hasGroup) {
        return { ...base, re_group: "", re_level: 1, re_num: 1 };
      }
      return { ...base, sort_order: "" };
    },
    async fetchRows() {
      try {
        const res = await this.$axios({
          method: "get",
          url: this.conf.url,
          params: { lang: "ko" },
          loadingBar: true,
        });
        if (res.status === 200) {
          this.rows = res.data || [];
          this.loaded = true;
        }
      } catch (e) {
        console.error(`${this.conf.noun} 목록 조회 실패:`, e);
        swal.fire("오류", apiErrorMessage(e, `${this.conf.noun} 목록을 불러오지 못했습니다.`), "error");
      }
    },
    openAdd() {
      this.form = this.emptyForm("add");
      this.original = null;
      this.$nextTick(() => this.$refs.codeInput?.focus());
    },
    openEdit(row) {
      const snapshot = {
        idx: row.idx,
        code: row.code || "",
        names: parseNames(row.names_json),
      };
      if (this.conf.hasGroup) {
        snapshot.re_group = row.re_group ?? "";
        snapshot.re_level = row.re_level ?? "";
        snapshot.re_num = row.re_num ?? "";
      } else {
        snapshot.sort_order = row.sort_order ?? "";
      }
      this.original = { ...snapshot, names: { ...snapshot.names } };
      this.form = { mode: "edit", ...snapshot, names: { ...snapshot.names } };
    },
    cancelForm() {
      this.form = null;
      this.original = null;
    },
    numOrNull(v) {
      return v === "" || v == null ? null : Number(v);
    },
    async save() {
      if (!this.canSave || this.saving) return;
      const f = this.form;
      this.saving = true;

      try {
        if (f.mode === "add") {
          // 등록: 비운 언어 칸은 보내지 않습니다 (조회 시 ko로 대체 표시).
          const names = {};
          NAME_LANGS.forEach((nl) => {
            if (f.names[nl.key]) names[nl.key] = f.names[nl.key];
          });
          const data = { code: f.code, names };
          if (this.conf.hasGroup) {
            data.re_group = this.numOrNull(f.re_group); // null → 맨 뒤('기타' 앞) 자동 채번
            data.re_level = this.numOrNull(f.re_level) ?? 1;
            data.re_num = this.numOrNull(f.re_num) ?? 1;
          } else if (f.sort_order !== "" && f.sort_order != null) {
            data.sort_order = Number(f.sort_order);
          }
          await this.$axios({ method: "post", url: this.conf.url, data, loadingBar: true });
        } else {
          // 수정: 전달한 필드만 반영되지만, names는 보내는 순간 6칸 전체가 교체되므로
          // 항상 6칸 전체를 보냅니다. 비운 칸은 그 언어 표시명 삭제로 처리됩니다(의도된 동작).
          const data = {};
          if (NAME_LANGS.some((nl) => f.names[nl.key] !== this.original.names[nl.key])) {
            data.names = { ...f.names };
          }
          if (this.conf.hasGroup) {
            if (String(f.re_group) !== String(this.original.re_group)) data.re_group = this.numOrNull(f.re_group);
            if (String(f.re_level) !== String(this.original.re_level)) data.re_level = this.numOrNull(f.re_level);
            if (String(f.re_num) !== String(this.original.re_num)) data.re_num = this.numOrNull(f.re_num);
          } else if (String(f.sort_order) !== String(this.original.sort_order)) {
            data.sort_order = this.numOrNull(f.sort_order);
          }
          await this.$axios({
            method: "put",
            url: `${this.conf.url}/${f.idx}`,
            data,
            loadingBar: true,
          });
        }

        this.cancelForm();
        await this.fetchRows();
        this.$emit("changed", { facet: this.facet, list: this.rows, deletedIdx: null });
        this.$notifyVue({
          type: "success",
          title: f.mode === "add" ? `${this.conf.noun}를 추가했습니다.` : `${this.conf.noun}를 수정했습니다.`,
        });
      } catch (e) {
        this.showFacetError(e);
      } finally {
        this.saving = false;
      }
    },
    async remove(row) {
      if (row.textbook_count > 0) return;

      // 표시명은 관리자가 입력하는 값이므로 html 옵션에 넣지 않습니다(관리자 간 stored-XSS 방지).
      const ok = await swal.fire({
        title: `${this.conf.noun} 삭제`,
        text: `'${row.name}' ${this.conf.noun}를 삭제하시겠습니까?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
        confirmButtonColor: "#d33",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "delete",
          url: `${this.conf.url}/${row.idx}`,
          loadingBar: true,
        });
        if (this.form && this.form.idx === row.idx) this.cancelForm();
        await this.fetchRows();
        this.$emit("changed", { facet: this.facet, list: this.rows, deletedIdx: row.idx });
        this.$notifyVue({ type: "success", title: `${this.conf.noun}를 삭제했습니다.` });
      } catch (e) {
        this.showFacetError(e, row);
      }
    },
    // 소속 교재 목록을 새 탭으로 — 편집 중인 교재 폼을 보호합니다.
    openFacetTextbooks(idx) {
      const url = this.$router.resolve({
        path: "/contents/textbooks",
        query: { [this.conf.listQueryKey]: idx },
      }).href;
      window.open(url, "_blank");
    },
    async showFacetError(e, row) {
      const status = apiErrorStatus(e);
      const detail = apiErrorMessage(e, "처리 중 오류가 발생했습니다.");
      console.error(`${this.conf.noun} API 오류:`, status, e);

      // 삭제 차단 409는 서버 문구가 이미 다음 행동을 안내하고 있어 그대로 노출합니다.
      if (status === 409 && row) {
        const r = await swal.fire({
          icon: "warning",
          title: "삭제할 수 없습니다",
          text: detail,
          showCancelButton: true,
          confirmButtonText: "소속 교재 보기",
          cancelButtonText: "닫기",
        });
        if (r.isConfirmed) this.openFacetTextbooks(row.idx);
        await this.fetchRows();
        return;
      }

      const map = {
        400: detail, // "코드는 필수입니다" / "names.ko는 필수입니다" 등 서버 한글 문구 그대로
        401: `관리자만 ${this.conf.noun}를 등록·수정·삭제할 수 있습니다.`,
        404: `이미 삭제된 ${this.conf.noun}입니다.`,
        409: "이미 존재하는 코드입니다. (대소문자 구분 없음)",
        422: detail,
      };
      swal.fire("오류", map[status] || detail, "error");

      // 404/409는 다른 관리자의 변경으로 목록이 낡았다는 신호이므로 재조회합니다.
      if (status === 404 || status === 409) await this.fetchRows();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.tf-modal__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.tf-modal__count {
  font-size: 13px;
  color: $p-ink-mid;

  strong { color: $p-ink; font-weight: 800; }
}

.tf-modal__form {
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  background: $p-panel-alt;

  &-title { font-size: 13px; font-weight: 800; color: $p-ink; margin-bottom: 10px; }

  &-grid {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) repeat(auto-fill, 110px);
    gap: 10px;
    align-items: start;
  }

  &-actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 12px;
  }
}

.tf-modal__names {
  margin-top: 12px;

  &-title {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;

    small { font-weight: 400; color: $p-ink-soft; }
  }

  &-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
  }
}

.tf-modal__field {
  display: flex;
  flex-direction: column;
  gap: 4px;

  span { font-size: 12px; font-weight: 700; color: $p-ink-mid; }
  small { font-weight: 400; color: $p-ink-soft; }

  &--num { max-width: 110px; }
}

.tf-modal__req { color: $p-err; }

.tf-modal__err { color: $p-err; font-size: 11px; }

.tf-modal__hint { color: $p-ink-soft; font-size: 11px; }

.tf-modal__code {
  display: inline-block;
  padding: 2px 6px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: 4px;
  font-family: $p-font-mono;
  font-size: 11px;
  font-weight: 700;
  color: $p-ink2;
}

.tf-modal__name { font-weight: 700; color: $p-ink; }

.tf-modal__cnt {
  font-weight: 700;
  color: $p-ink;

  &.is-zero { color: $p-ink-soft; font-weight: 400; }
}

.tf-modal__row-actions {
  display: inline-flex;
  gap: 4px;
}

// disabled 버튼은 포인터 이벤트가 죽어 툴팁이 뜨지 않으므로 래퍼에 title을 겁니다.
.tf-modal__del-wrap { display: inline-flex; }

// 목록 세로 스크롤 시 헤더 상단 고정
.tf-modal__table {
  :deep(.a-table-wrap) {
    max-height: 50vh;
    overflow-y: auto;
  }

  :deep(.a-table__th) {
    position: sticky;
    top: 0;
    z-index: 2;
    box-shadow: inset 0 -1px 0 $p-line;
  }
}
</style>
